import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EuiPopover,
  EuiListGroup,
  EuiListGroupItem,
  EuiText,
  EuiHorizontalRule,
  EuiSpacer,
  EuiAvatar,
  EuiHeaderSectionItemButton,
  EuiConfirmModal,            // NEW: 二次确认弹窗
  EuiOverlayMask,            // NEW: 弹窗遮罩
} from "@elastic/eui";
import { TranslateLanguage } from "../components/KeywordTranslator";
import { useModal } from "../ModalContext";
import { jwtDecode } from "jwt-decode";

/** --- 工具函数：从用户名/邮箱生成稳定颜色（HSL） --- */
function stringToHsl(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const h = Math.abs(hash) % 360;         // 0-359
  const s = 60;                            // 稍微鲜艳
  const l = 55;                            // 中等亮度
  return `hsl(${h}, ${s}%, ${l}%)`;
}

/** --- 取显示名与首字母（中英文都友好） --- */
function getDisplayAndInitials(username, email) {
  const base = username || email || "Guest";
  // 若是邮箱，取 @ 前；否则整段
  const namePart = base.includes("@") ? base.split("@")[0] : base;
  // 取第一个“可见字符”（支持中文/英文/数字）
  const first = (namePart.trim()[0] || "G").toUpperCase();
  return {
    displayName: base,
    initials: first,
  };
}

/** 从字符串稳定映射到一个 hex 颜色（调色板挑选） */
function stringToHexFromPalette(str = "Guest") {
  const palette = [
    "#54B399", "#6092C0", "#D36086", "#9170B8", "#CA8EAE",
    "#D6BF57", "#B9A888", "#DA8B45", "#AA6556", "#E7664C",
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const idx = Math.abs(hash) % palette.length;
  return palette[idx];
}
function stringToHexFromDarkPalette(str = "Guest") {
  const darkPalette = [
    "#1F3A93", "#0B3D91", "#3C1874", "#5B2C6F", "#7B241C",
    "#922B21", "#145A32", "#0E6655", "#873600", "#4A235A",
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % darkPalette.length;
  return darkPalette[idx];
}

export default function TopRightMenuAvatar({ withLanguage = "1" }) {
  const navigate = useNavigate();
  const { setLoginModalVisible, setRegisterModalVisible } = useModal();

  // ---------- 语言 ----------
  const browserLangIsSimplified = (navigator.language || navigator.userLanguage) === "zh-CN";
  const defaultTag = browserLangIsSimplified ? "S" : "T";
  const initialLang = localStorage.userLanguage ? localStorage.userLanguage : defaultTag;
  const [language, setLanguage] = useState(initialLang);
  useMemo(() => {
    localStorage.userLanguage = language;
  }, [language]);

  const onChangeLanguage = () => {
    const next = language === "T" ? "S" : "T";
    setLanguage(next);
    localStorage.userLanguage = next;
    window.scrollTo(0, 0);
    window.location.reload();
  };

  // ---------- 用户 & 角色 ----------
  const safeUserObj = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  })();
  const [username, setUsername] = useState(safeUserObj ? safeUserObj.username : null);
  const email = safeUserObj?.email || null;

  const token = sessionStorage.getItem("token");
  let userRole = "";
  if (token) {
    try {
      const decoded = jwtDecode(token);
      userRole = decoded?.role || "";
    } catch {
      userRole = "";
    }
  }
  
  const isAdmin = userRole === "Admin"; // NEW

  // ---------- 头像显示名 / 首字母 / 颜色 ----------
  const { displayName, initials } = getDisplayAndInitials(username, email);
  const avatarColor = stringToHsl(displayName);

    // ---------- 删除账号（软删除：后端将 Role 改为 Deleted） ----------
  const [isDeleteOpen, setIsDeleteOpen] = useState(false); // NEW: 控制确认弹窗
  const [isDeleting, setIsDeleting] = useState(false);     // NEW: 提交中

  // ---------- 行为 ----------
  // 调用后端删除当前账号（软删除）
  const markMyAccountDeleted = async () => {
    const url = "/api/users/me"; // ✅ 对应后端 DELETE /api/users/me

    const resp = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!resp.ok) {
      const msg = await resp.text().catch(() => "");
      throw new Error(msg || `HTTP ${resp.status}`);
    }
    return true;
  };

  const handleConfirmDelete = async () => {
    if (isAdmin) { // 双保险：管理员不能自删
      alert(TranslateLanguage(language, "管理員帳號不能刪除自己，請聯繫其他管理員。"));
      setIsDeleteOpen(false);
      return;
    }
    setIsDeleting(true);
    try {
      await markMyAccountDeleted();
      // 清理本地状态并登出
      localStorage.removeItem("username");
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("token");
      setUsername(null);
      setIsDeleteOpen(false);
      setIsOpen(false);
      navigate("/");
      // 也可选：alert/通知
      alert(TranslateLanguage(language, "您的帳號已刪除，並已登出。"));
    } catch (e) {
      console.error(e);
      alert(
        TranslateLanguage(language, "刪除失敗，請稍後再試或聯繫客服。") +
          (e?.message ? `\n${e.message}` : "")
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    if (isAdmin) { // NEW: 入口处直接拦截
      alert(TranslateLanguage(language, "管理員帳號不能刪除自己。"));
      return;
    }
    setIsDeleteOpen(true);
  };

  const handleSettingClick = () => {
    if (username) navigate("/settingspage");
    else navigate("/settings");
    setIsOpen(false);
  };

  const handlePlaylistClick = () => {
    if (username) navigate("/playerpage");
    else navigate("/player");
    setIsOpen(false);
  };

  const handleFavoriteClick = () => {
    if (username) navigate("/favoritepage");
    else navigate("/sharepage");
    setIsOpen(false);
  };

  const handleReadingPlansClick = () => {
    navigate("/reading-plans");
    setIsOpen(false);
  };

  const handleBibleNotesClick = () => {
    navigate("/bible-notes");
    setIsOpen(false);
  };

  const onLogout = () => {
    localStorage.removeItem("username");
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    setUsername(null);
    setIsOpen(false);
    // Hard navigation clears SearchProvider state and removes q/reading-plan parameters.
    window.location.replace("/");
  };

  const onManagement = () => {
    setIsOpen(false);
    navigate("/adminpage");
  };

  // ---------- Popover ----------
  const [isOpen, setIsOpen] = useState(false);
  const howToUseHref =
    language === "T"
      ? "https://help.checkthebible.com/user-guide-chinese-traditional-english"
      : "https://help.checkthebible.com/user-guide-chinese-simplified-english";

  return (
    // <div style={{ position: "fixed", top: 12, right: 12, zIndex: 1000 }}>
    <div>
      <EuiPopover
        isOpen={isOpen}
        closePopover={() => setIsOpen(false)}
        anchorPosition="downRight"
        panelPaddingSize="s"
        button={
            <EuiHeaderSectionItemButton onClick={() => setIsOpen(!isOpen)}>
            {username ? (
                <EuiAvatar
                name={displayName}
                initials={initials}
                color={stringToHexFromDarkPalette(displayName)}
                size="m"
                />
            ) : (
                <div
                style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "#520e63ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: "14px",
                }}
                >
                {TranslateLanguage(language, "登入")}
                </div>
            )}
            </EuiHeaderSectionItemButton>
        }
      >
        <div style={{ minWidth: 260 }}>
          <EuiText size="s">
            <strong>
              {username
                ? `${TranslateLanguage(language, "歡迎，")}${username}`
                : TranslateLanguage(language, "訪客")}
            </strong>
          </EuiText>

          <EuiSpacer size="s" />
          <EuiHorizontalRule margin="s" />

            {/* 登录/注册 或 退出/管理 */}
            {username && (
            <EuiListGroupItem
                label={TranslateLanguage(language, "登出")}
                onClick={onLogout}
                iconType="exit"
                size="s"
            />
            )}

            {username && userRole === "Admin" && (
            <EuiListGroupItem
                label={TranslateLanguage(language, "管理")}
                onClick={onManagement}
                iconType="gear"
                size="s"
            />
            )}

            {!username && (
            <EuiListGroupItem
                label={TranslateLanguage(language, "登入")}
                onClick={() => {
                setIsOpen(false);
                setLoginModalVisible(true);
                }}
                iconType="lock"
                size="s"
            />
            )}

            {!username && (
            <EuiListGroupItem
                label={TranslateLanguage(language, "註冊")}
                onClick={() => {
                setIsOpen(false);
                setRegisterModalVisible(true);
                }}
                iconType="plusInCircle"
                size="s"
            />
            )}

            {/* NEW: 删除帐号（登录后显示） */}
            {username && !isAdmin && (
              <EuiListGroupItem
                label={TranslateLanguage(language, "刪除帳號")}
                onClick={handleDeleteClick}
                iconType="trash"
                size="s"
              />
            )}

            <EuiHorizontalRule margin="s" />

            {/* 设置 / 语言 */}
            <EuiListGroupItem
              label={TranslateLanguage(language, "我的設定")}
              onClick={handleSettingClick}
              iconType="gear"
              size="s"
            />
            <EuiListGroupItem
              label={TranslateLanguage(language, "我的歌單")}
              onClick={handlePlaylistClick}
              iconType="play"
              size="s"
            />
            <EuiListGroupItem
              label={TranslateLanguage(language, "收藏分享")}
              onClick={handleFavoriteClick}
              iconType="heart"
              size="s"
            />
            <EuiListGroupItem
              label={TranslateLanguage(language, "讀經計劃")}
              onClick={handleReadingPlansClick}
              iconType="calendar"
              size="s"
            />
            <EuiListGroupItem
              label={TranslateLanguage(language, "我的聖經筆記")}
              onClick={handleBibleNotesClick}
              iconType="pencil"
              size="s"
            />
            {/* {withLanguage === "1" && (
              <EuiListGroupItem
                label={language === "T" ? "简体" : "繁體"}
                onClick={onChangeLanguage}
                iconType="globe"
                size="s"
              />
            )} */}

            <EuiHorizontalRule margin="s" />

          <EuiListGroup flush maxWidth="100%">
            {/* 导航 */}
            <EuiListGroupItem
              label={TranslateLanguage(language, "首頁")}
              onClick={() => {
                window.location.replace("/");
                setIsOpen(false);
              }}
              iconType="home"
              size="s"
            />
            <EuiListGroupItem
              label={TranslateLanguage(language, "如何使用")}
              href={howToUseHref}
              target="_blank"
              iconType="help"
              size="s"
            />
            <EuiListGroupItem
              label={TranslateLanguage(language, "有關我們")}
              href="https://www.checkthebible.org/about"
              target="_blank"
              iconType="iInCircle"
              size="s"
            />
            <EuiListGroupItem
              label={TranslateLanguage(language, "異象")}
              href="https://www.checkthebible.org/vision"
              target="_blank"
              iconType="discoverApp"
              size="s"
            />
            <EuiListGroupItem
              label={TranslateLanguage(language, "事工")}
              href="https://www.checkthebible.org/mission"
              target="_blank"
              iconType="logoGCPMono"
              size="s"
            />
            <EuiListGroupItem
              label={TranslateLanguage(language, "與我們聯繫")}
              href="https://www.checkthebible.org/contact"
              target="_blank"
              iconType="email"
              size="s"
            />
          </EuiListGroup>
        </div>
      </EuiPopover>
      {/* NEW: 删除确认弹窗 */}
      {isDeleteOpen && (
        <EuiOverlayMask>
          <EuiConfirmModal
            title={TranslateLanguage(language, "刪除帳號？")}
            onCancel={() => setIsDeleteOpen(false)}
            onConfirm={handleConfirmDelete}
            cancelButtonText={TranslateLanguage(language, "取消")}
            confirmButtonText={isDeleting ? TranslateLanguage(language, "刪除中…") : TranslateLanguage(language, "確認刪除")}
            buttonColor="danger"
            defaultFocusedButton="confirm"
            confirmButtonDisabled={isDeleting}
          >
            <p style={{ marginTop: 0 }}>
              {TranslateLanguage(
                language,
                "此操作將刪除您的帳號與相關數據，並立即登出。此動作不可恢復，是否確認？"
              )}
            </p>
          </EuiConfirmModal>
        </EuiOverlayMask>
      )}
    </div>
  );
}
